import { Color, ShaderMaterial, UniformsLib, UniformsUtils } from 'three';
import type { IUniform, Side, Texture } from 'three';
import type { SharedUniforms } from '../shaders/shared';

/** All procedural maps the world materials sample. */
export interface MaterialTextures {
  brush: Texture;
  leaf: Texture;
  rippleShallow: Texture;
  rippleSteep: Texture;
  sandGrain: Texture;
  glitter: Texture;
  bark: Texture;
  rock: Texture;
  ground: Texture;
}

/** A ShaderMaterial wired for three's lights/shadows plus our shared world uniforms. */
export function litMaterial(
  shared: SharedUniforms,
  local: Record<string, IUniform>,
  opts: { name: string; vertexShader: string; fragmentShader: string; defines?: Record<string, string | number | boolean>; vertexColors?: boolean; side?: Side },
): ShaderMaterial {
  const m = new ShaderMaterial({
    name: opts.name,
    uniforms: { ...UniformsUtils.clone(UniformsLib.lights), ...shared.all(), ...local },
    vertexShader: opts.vertexShader,
    fragmentShader: opts.fragmentShader,
    defines: opts.defines ?? {},
    lights: true,
    fog: false,
    vertexColors: opts.vertexColors ?? false,
  });
  if (opts.side !== undefined) m.side = opts.side;
  return m;
}

export const col = (hex: string): IUniform<Color> => ({ value: new Color(hex) });

/** GLSL: unpack a tangent-space normal map sample. */
export const UNPACK = /* glsl */ `
vec3 unpackN(vec4 t) { return normalize(t.xyz * 2.0 - 1.0); }
// Triplanar normal mapping (whiteout blend) for world-space surfaces.
vec3 triNormal(sampler2D tex, vec3 p, vec3 n, float scale, float strength) {
  vec3 w = pow(abs(n), vec3(4.0));
  w /= (w.x + w.y + w.z);
  vec3 tx = unpackN(texture2D(tex, p.zy * scale));
  vec3 ty = unpackN(texture2D(tex, p.xz * scale));
  vec3 tz = unpackN(texture2D(tex, p.xy * scale));
  tx = vec3(tx.xy * strength + n.zy, abs(tx.z) * n.x);
  ty = vec3(ty.xy * strength + n.xz, abs(ty.z) * n.y);
  tz = vec3(tz.xy * strength + n.xy, abs(tz.z) * n.z);
  return normalize(tx.zyx * w.x + ty.xzy * w.y + tz.xyz * w.z);
}
float triHeight(sampler2D tex, vec3 p, vec3 n, float scale) {
  vec3 w = pow(abs(n), vec3(4.0));
  w /= (w.x + w.y + w.z);
  return texture2D(tex, p.zy * scale).a * w.x + texture2D(tex, p.xz * scale).a * w.y + texture2D(tex, p.xy * scale).a * w.z;
}
`;
